/**
 * Law Firm Account Management Types
 *
 * This module provides comprehensive type definitions for law firm account management,
 * including user management, firm profiles, invitations, and related operations.
 */

import { z } from 'zod';
import type { ThemeChoice } from '@/lib/utils/theme';
import { EMAIL_BODY_FONT_FAMILIES } from '@/lib/constants/email-typography';
import type {
  EmailBodyFontFamily,
  EmailBodyFontSizePt
} from '@/lib/constants/email-typography';

// ============================================================================
// Base Types
// ============================================================================

/**
 * Backend address format (matches law-firms-backend PostalAddressDto)
 */
export interface BackendAddress {
  businessName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  poBox?: string;
  country?: string;
}

/**
 * Frontend address format (for display compatibility)
 */
export interface Address {
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

/**
 * Bank account details for trust accounts
 */
export interface BankAccount {
  accountName: string;
  accountNumber: string;
  bsb: string;
}

/**
 * Primary contact information for a law firm
 */
export interface PrimaryContact {
  selectedUserId?: string;
  fullName: string;
  email: string;
  phone: string;
  title?: string;
}

/**
 * Correspondence preferences for communications (frontend display format)
 */
export interface CorrespondencePreferences {
  replyToOption: 'user' | 'firm' | 'generic';
  showUserContact: boolean;
  genericReplyEmail?: string;
}

/**
 * Backend correspondence configuration (matches actual backend DTO)
 */
export interface CorrespondenceConfig {
  defaultSignatory?: 'authenticated_user' | 'solicitor_with_carriage';
  includeSignerNameAfterSignature?: boolean;
  includeSignerTitleAfterSignature?: boolean;
  /**
   * Whether users may override the firm-wide email signature with a personal
   * one. **Default `false`** (VALE-445) — the firm signature plus merge fields
   * is the intended answer, and a personal signature is an exception an admin
   * grants.
   *
   * Enforced on the backend's write path as well as by hiding the editor, so a
   * stale tab cannot save one.
   */
  allowPersonalEmailSignatureOverride?: boolean;
  /**
   * Whether Vale appends an HTML email signature to outbound email at all.
   * **Default `true`** (VALE-490) — so an absent key means enabled, and every
   * read must treat `undefined` as on rather than off.
   *
   * Off suppresses every Vale-side signature, personal as well as firm-wide, for
   * firms whose Outlook integration already signs their mail. It never destroys
   * the stored signature content.
   */
  htmlEmailSignatureEnabled?: boolean;
  outboundCcEmail?: string | null;
  /**
   * Grammatical person of institution-correspondence narrative text: 'we'
   * writes on behalf of the firm, 'individual' on behalf of the signatory.
   */
  firmVoice?: 'we' | 'individual';
  /**
   * Whether this firm's letters print on a **per-office** letterhead (VALE-540).
   * Default `false` — one firm-wide letterhead, which is what every firm has.
   *
   * Gates resolution as well as the Branding card's list, so switching it off
   * genuinely reverts a firm's output. Nothing is destroyed: an office precedent
   * already uploaded starts printing again the moment it is switched back on.
   */
  perOfficeLetterhead?: boolean;
  /**
   * The font outbound **email bodies** are written in (VALE-574). Absent means
   * Vale's default, Arial 11pt.
   *
   * Email only — a letter's typography belongs to the firm's own letterhead
   * document, so this never affects a generated letter or PDF.
   */
  emailBodyTypography?: {
    fontFamily?: EmailBodyFontFamily;
    fontSizePt?: EmailBodyFontSizePt;
  };
}

/**
 * How much of the distribution a beneficiary declaration may recite.
 *
 * Mirrors the backend's `DECLARATION_AMOUNT_DISCLOSURE_VALUES`. Declared once
 * here as a value so the config interface, the request schema and the settings
 * control all read from it — a second literal is how a picker ends up offering
 * an option the backend rejects.
 */
export const DECLARATION_AMOUNT_DISCLOSURE_VALUES = ['own', 'all'] as const;
export type DeclarationAmountDisclosure =
  (typeof DECLARATION_AMOUNT_DISCLOSURE_VALUES)[number];

/** What a firm gets when it has never opened the setting. */
export const DEFAULT_DECLARATION_AMOUNT_DISCLOSURE: DeclarationAmountDisclosure =
  'own';

/**
 * Law firm configuration object (backend format)
 */
export interface LawFirmConfiguration {
  correspondence?: CorrespondenceConfig;
  notifications?: {
    emailNotificationsEnabled?: boolean;
  };
  tags?: {
    mode?: 'personal' | 'law-firm-wide';
  };
  distributions?: {
    includeBankDetailsInAdvice?: boolean;
    declarationsRequired?: boolean;
    /**
     * How much of the distribution a beneficiary declaration may recite:
     * `'own'` (the default) limits it to the recipient's own share and amount,
     * `'all'` allows the full who-gets-what table. Enforced on the backend when
     * the document is generated, not by how a firm authored its template.
     */
    declarationAmountDisclosure?: DeclarationAmountDisclosure;
    sendModalDefaults?: {
      includeBankVerification?: boolean;
      includeDeclaration?: boolean;
    };
  };
}

// ============================================================================
// Law Firm Types
// ============================================================================

/**
 * File information for letterhead
 */
export interface LetterheadFile {
  id: number;
  originalFilename: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  s3Key: string;
  imageS3Key?: string | null;
  mimeType: string;
  fileType: string;
  extractedData?: Record<string, unknown>;
  isCertifiedCopy: boolean;
  metadata?: Record<string, unknown>;
  analysisMetadata?: Record<string, unknown>;
  fileSizeKb?: number | null;
  deletedAt?: string | null;
}

/**
 * Complete law firm profile information
 */
export interface LawFirmProfile {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  valeEmail: string;
  trustAccountNeedInfo: string;
  verifiedAt: string | null;
  postalAddress?: BackendAddress;
  businessAddress?: BackendAddress;
  trustBankAccount?: BankAccount;
  officeBankAccount?: BankAccount;
  apcaUserId?: string;
  primaryContact?: PrimaryContact;
  primaryContactUser?: {
    id: number;
  };
  correspondencePreferences?: CorrespondencePreferences;
  letterheadFilename?: string;
  letterHeadFileId?: number;
  letterHeadFile?: LetterheadFile;
  htmlEmailSignature?: string;
  configuration?: LawFirmConfiguration;
  membershipStatus?:
    | 'pending'
    | 'trial'
    | 'active'
    | 'active-free'
    | 'suspended'
    | 'cancelled'; // Current membership status
  membershipTier?: 'standard' | 'professional' | 'enterprise'; // Current membership tier
  trialStartedAt?: string | null; // ISO date when trial period started
  trialExpiresAt?: string | null; // ISO date when trial period expires
  membershipActivatedAt?: string | null; // ISO date when membership was activated
  nextBillingDate?: string | null; // ISO date of the next billing boundary (anchored at activation for a new firm)
  scheduledCancellationDate?: string | null; // ISO date when subscription will end if cancellation is pending
  // Invoice / billing details — overrides for what appears on Vale invoices.
  // Null = BE falls back to the corresponding firm-level default.
  billingName?: string | null;
  billingEmail?: string | null;
  billingAbn?: string | null;
  billingAddress?: BackendAddress | null;
  faviconUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request payload for updating law firm profile
 */
export interface UpdateLawFirmProfileRequest {
  name?: string;
  email?: string;
  phoneNumber?: string;
  trustAccountNeedInfo?: string;
  postalAddress?: BackendAddress;
  businessAddress?: BackendAddress;
  trustBankAccount?: BankAccount;
  officeBankAccount?: BankAccount;
  apcaUserId?: string;
  primaryContact?: PrimaryContact;
  primaryContactUser?: {
    id: number;
  };
  correspondencePreferences?: CorrespondencePreferences; // Legacy frontend format
  configuration?: LawFirmConfiguration; // New backend format
}

// ============================================================================
// User Types
// ============================================================================

/**
 * User status within the law firm
 */
export type UserStatus = 'pending' | 'invited' | 'active' | 'deactivated';

/**
 * Structured user preferences to replace generic Record type
 */
export interface MatterViewPreferences {
  tags?: string[];
  types?: string[];
  teamMembers?: string[];
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  sortOrder?: 'ASC' | 'DESC';
}

export interface UserPreferences {
  /**
   * The user's persisted light/dark/system CHOICE (VALE-548). Written only when
   * the theme toggle is actually used — never on an OS-preference change, which
   * is why it answers "what did people choose" rather than "what did they see".
   *
   * This replaced a dead `ui: { theme, language, timezone }` bag that nothing
   * ever populated or read; the backend's preferences bag holds `theme` at the
   * top level, beside `notifications` and `correspondence`.
   */
  theme?: ThemeChoice;
  correspondence?: {
    forwardCorrespondenceReplies?: boolean;
  };
  notifications?: {
    emailNotificationsEnabled?: boolean;
    smsNotificationsEnabled?: boolean;
    pushNotificationsEnabled?: boolean;
  };
  privacy?: {
    shareDataWithPartners?: boolean;
    allowAnalytics?: boolean;
  };
  matterViews?: {
    all?: MatterViewPreferences;
    archive?: MatterViewPreferences;
  };
}

/**
 * Law firm user profile
 */
export interface LawFirmUser {
  id: number;
  email: string;
  /**
   * All three name columns are **nullable** — `law_firm_users.first_name` /
   * `middle_name` / `last_name` are `nullable: true`, and the backend's own
   * `AccountUserResponseDto` publishes them as `string | null`. A firm user who
   * was invited and has not accepted yet (or one created through the ops
   * portal, which bypasses the invite DTO's `@IsNotEmpty`) has none of them.
   *
   * These were declared non-nullable until LF-6015, which is how the account
   * header came to render the literal string `null null` and to throw
   * `TypeError: Cannot read properties of null (reading 'charAt')` on the
   * initials. Render them through `getUserDisplayName` / `getUserInitials`
   * (`lib/utils/permissions.ts`), which fall back to the non-null `email`.
   */
  firstName: string | null;
  middleName?: string | null;
  lastName: string | null;
  role: string;
  status: UserStatus;
  /**
   * The office this person works out of (VALE-540), or `null` when not recorded
   * — which is a permanently valid state rather than a gap, and what every user
   * is in until their firm both creates offices and assigns them.
   *
   * Feeds the `office.*` email-signature merge fields. It is **not** what decides
   * a letter's letterhead — that is the matter's office, never the signer's.
   */
  officeId?: number | null;
  preference?: UserPreferences;
  handwrittenSignatureFile?: {
    id: number;
    originalFilename: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    s3Key: string;
    imageS3Key?: string | null;
    mimeType: string;
    fileType: string;
    extractedData?: Record<string, unknown>;
    isCertifiedCopy: boolean;
    metadata?: Record<string, unknown>;
    analysisMetadata?: Record<string, unknown>;
    fileSizeKb?: number | null;
    deletedAt?: string | null;
  };
  emailSignature?: string;
  /**
   * Direct phone number. Rendered by the `<<lawyer.phoneNumber>>` merge field in
   * signatures, which is why the team page surfaces it as a fillable column
   * (VALE-445) — before that it existed on the API and nowhere in the product,
   * so no user anywhere had one recorded.
   */
  phoneNumber?: string | null;
  /**
   * Job title / position, as one free-text line including any post-nominals.
   * Rendered by `<<lawyer.title>>`. **Not** {@link role}, which is the internal
   * Vale permission and must never reach a third party.
   */
  title?: string | null;
  createdAt: string;
  updatedAt: string;
  invitedAt?: string;
  lawFirm?: LawFirmProfile;
  isAdministrator?: boolean;
  hasSignatureAccess?: boolean;
  totpEnabledAt?: string | null;
}

/**
 * Request payload for updating a law firm user
 */
export interface UpdateLawFirmUserRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  /** Empty string clears it. */
  title?: string;
  role?: string;
  status?: UserStatus;
  emailSignature?: string;
  signatureFilename?: string;
  preference?: UserPreferences;
  /**
   * The office this person works out of (VALE-540). `null` clears it — "not
   * recorded" is a permanently valid state, not a gap.
   *
   * Feeds the `office.*` email-signature merge fields. It does **not** decide
   * which letterhead a letter prints on: that comes from the matter's office,
   * never from the signer's.
   */
  officeId?: number | null;
}

// ============================================================================
// Invitation Types
// ============================================================================

/**
 * Individual user invitation data
 */
export interface UserInvitation {
  firstName: string;
  lastName: string;
  email: string;
  /**
   * Optional at invite (VALE-445) — the two facts a firm signature's merge
   * fields need, captured while the admin is already thinking about this person.
   */
  phoneNumber?: string;
  title?: string;
}

/**
 * Request to invite users to the law firm
 */
export interface InviteUsersRequest {
  users: UserInvitation[]; // Array of user objects with names and emails
  role?: string; // Optional default role for all invitees
  message?: string; // Optional personalized message
}

/**
 * Result of a single invitation attempt
 */
export interface InvitationResult {
  email: string;
  success: boolean;
  userId?: number;
  error?: string;
  invitationToken?: string;
}

/**
 * Response from bulk invitation request
 */
export interface InviteUsersResponse {
  results: InvitationResult[];
  successCount: number;
  failureCount: number;
  summary: string;
}

/**
 * Information about a pending invitation
 */
export interface PendingInvitation {
  userId: number;
  email: string;
  invitedAt: string;
  expiresAt: string;
  invitedBy: string;
  status: 'pending' | 'expired' | 'accepted';
}

// ============================================================================
// Action Result Types
// ============================================================================

/**
 * Generic result type for account management operations using discriminated unions for type safety
 */
export type AccountActionResult<T = unknown> =
  | {
      success: true;
      data: T;
      message?: string;
    }
  | {
      success: false;
      error: string;
      validationErrors?: Record<string, string[]>;
      /**
       * The backend refused the write until the user re-authenticates
       * (`STEP_UP_REQUIRED`, VALE-465 / BANKSEC-3) — set when changing a trust
       * account that already has details in it. The caller runs
       * `runStepUpChallenge()` and retries the identical payload; see
       * `lib/auth/step-up.ts`.
       */
      stepUpRequired?: boolean;
      /**
       * The server-signed ticket that must accompany the replay. It carries the
       * moment of refusal and a fingerprint of the pending change, so the
       * re-authentication is bound to *this* submission — a recent login cannot
       * substitute for it. Opaque to the client: carry it back unmodified.
       */
      stepUpTicket?: string;
    };

// ============================================================================
// List and Filter Types
// ============================================================================

/**
 * Filter options for listing law firm users
 */
export interface UserListFilters {
  status?: UserStatus | UserStatus[];
  role?: string;
  search?: string;
  isAdministrator?: boolean;
  hasSignatureAccess?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'email' | 'role' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated response for user lists
 */
export interface UserListResponse {
  users: LawFirmUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================================================
// Permission Types
// ============================================================================

/**
 * User permissions within the law firm
 */
export interface UserPermissions {
  canManageUsers: boolean;
  canUpdateFirmProfile: boolean;
  canViewAllMatters: boolean;
  canManageTemplates: boolean;
  canViewReports: boolean;
  canManageBilling: boolean;
}

// ============================================================================
// Statistics Types
// ============================================================================

/**
 * Law firm statistics and metrics
 */
export interface LawFirmStatistics {
  totalUsers: number;
  activeUsers: number;
  pendingInvitations: number;
  totalMatters: number;
  activeMatters: number;
  completedMatters: number;
  storageUsed: number; // in bytes
  storageLimit: number; // in bytes
  lastActivityAt: string;
}

// ============================================================================
// Validation Schemas
// ============================================================================

/**
 * Backend address validation schema (for API requests)
 */
export const BackendAddressSchema = z.object({
  businessName: z.string().max(100).optional(),
  streetAddress: z.string().max(100).optional(),
  suburb: z.string().max(50).optional(),
  state: z.string().max(20).optional(),
  postcode: z.string().max(10).optional(),
  poBox: z.string().max(20).optional(),
  country: z.string().max(50).optional()
});

/**
 * Bank account validation schema
 */
export const BankAccountSchema = z.object({
  accountName: z.string().min(1, 'Account name is required').max(100),
  accountNumber: z.string().min(1, 'Account number is required').max(20),
  bsb: z.string().regex(/^\d{3}-?\d{3}$/, 'Invalid BSB format')
});

/**
 * Primary contact validation schema
 */
export const PrimaryContactSchema = z.object({
  selectedUserId: z.string().optional(),
  fullName: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Invalid email format'),
  phone: z.string().min(1, 'Phone is required').max(20),
  title: z.string().optional()
});

const LawFirmConfigurationSchema = z
  .object({
    correspondence: z
      .object({
        defaultSignatory: z
          .enum(['authenticated_user', 'solicitor_with_carriage'])
          .optional(),
        includeSignerNameAfterSignature: z.boolean().optional(),
        includeSignerTitleAfterSignature: z.boolean().optional(),
        allowPersonalEmailSignatureOverride: z.boolean().optional(),
        htmlEmailSignatureEnabled: z.boolean().optional(),
        outboundCcEmail: z.string().nullable().optional(),
        // Load-bearing, not decorative: UpdateLawFirmProfileRequestSchema
        // derives from this schema and updateLawFirmProfileAction sends the
        // PARSED result, so a key missing here is silently stripped from the
        // PATCH and the setting appears to save while changing nothing.
        firmVoice: z.enum(['we', 'individual']).optional(),
        perOfficeLetterhead: z.boolean().optional(),
        // Same load-bearing note as firmVoice above: omitted here, the key is
        // stripped from the PATCH and the font appears to save while every
        // email keeps going out in the old one.
        emailBodyTypography: z
          .object({
            fontFamily: z.enum(EMAIL_BODY_FONT_FAMILIES).optional(),
            fontSizePt: z
              .union([z.literal(10), z.literal(11), z.literal(12)])
              .optional()
          })
          .optional()
      })
      .optional(),
    notifications: z
      .object({
        emailNotificationsEnabled: z.boolean().optional()
      })
      .optional(),
    tags: z
      .object({
        mode: z.enum(['personal', 'law-firm-wide']).optional()
      })
      .optional(),
    distributions: z
      .object({
        includeBankDetailsInAdvice: z.boolean().optional(),
        declarationsRequired: z.boolean().optional(),
        declarationAmountDisclosure: z
          .enum(DECLARATION_AMOUNT_DISCLOSURE_VALUES)
          .optional(),
        sendModalDefaults: z
          .object({
            includeBankVerification: z.boolean().optional(),
            includeDeclaration: z.boolean().optional()
          })
          .optional()
      })
      .optional()
  })
  .optional();

/**
 * Law firm profile validation schema
 */
export const LawFirmProfileSchema = z.object({
  id: z.number(),
  name: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().min(1).max(20),
  valeEmail: z.string().email(),
  trustAccountNeedInfo: z.string(),
  verifiedAt: z.string().nullable(),
  postalAddress: BackendAddressSchema.optional(),
  businessAddress: BackendAddressSchema.optional(),
  trustBankAccount: BankAccountSchema.optional(),
  primaryContact: PrimaryContactSchema.optional(),
  correspondencePreferences: z
    .object({
      replyToOption: z.enum(['user', 'firm', 'generic']),
      showUserContact: z.boolean(),
      genericReplyEmail: z.string().email().optional()
    })
    .optional(),
  letterheadFilename: z.string().optional(),
  configuration: LawFirmConfigurationSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});

/**
 * Law firm user validation schema
 */
export const LawFirmUserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  firstName: z.string().min(1).max(50),
  middleName: z.string().max(50).optional(),
  lastName: z.string().min(1).max(50),
  phoneNumber: z.string().max(30).nullable().optional(),
  title: z.string().max(200).nullable().optional(),
  role: z.string().min(1).max(100),
  status: z.enum(['pending', 'invited', 'active', 'deactivated']),
  officeId: z.number().int().positive().nullable().optional(),
  preference: z.record(z.unknown()).optional(),
  handwrittenSignatureFile: z
    .object({
      id: z.number(),
      originalFilename: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      status: z.string(),
      s3Key: z.string(),
      imageS3Key: z.string().nullable().optional(),
      mimeType: z.string(),
      fileType: z.string(),
      extractedData: z.record(z.unknown()).optional(),
      isCertifiedCopy: z.boolean(),
      metadata: z.record(z.unknown()).optional(),
      analysisMetadata: z.record(z.unknown()).optional(),
      fileSizeKb: z.number().nullable().optional(),
      deletedAt: z.string().nullable().optional()
    })
    .optional(),
  emailSignature: z.string().max(1000).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  invitedAt: z.string().optional(),
  lawFirm: LawFirmProfileSchema.optional(),
  isAdministrator: z.boolean().optional(),
  hasSignatureAccess: z.boolean().optional()
});

/**
 * Update law firm profile request validation schema
 */
export const UpdateLawFirmProfileRequestSchema = LawFirmProfileSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  verifiedAt: true
}).partial();

/**
 * Update law firm user request validation schema
 */
/**
 * **Every field a caller may PUT must be picked here.** `updateLawFirmUserAction`
 * sends `safeParse`'s *output*, so a key absent from this schema is stripped
 * **without an error** — the request succeeds, the field is silently dropped, and
 * nothing anywhere reports it. That is exactly how the profile's Phone row and
 * the team page's Phone cell both appeared to save and changed nothing.
 *
 * `phoneNumber` and `title` are re-declared rather than picked: the read shape on
 * {@link LawFirmUserSchema} is nullable (the columns are), but the backend DTO
 * takes a `string` and treats empty as a clear — so `null` would be rejected by
 * its `@IsString()`.
 */
export const UpdateLawFirmUserRequestSchema = LawFirmUserSchema.pick({
  firstName: true,
  middleName: true,
  lastName: true,
  email: true,
  role: true,
  status: true,
  emailSignature: true,
  preference: true,
  /*
    Picked deliberately (VALE-540). `updateLawFirmUserAction` sends the
    **zod-parsed** body, so a key absent here is stripped with no error at all —
    the office would appear to save and change nothing. `null` is kept, because
    clearing the assignment is a real instruction the backend honours.
  */
  officeId: true
})
  .partial()
  .extend({
    phoneNumber: z.string().max(30).optional(),
    title: z.string().max(200).optional()
  });
